import React, { useEffect, useRef, useState } from "react";
import { useModalBackdropClose } from "../hooks/useModalBackdropClose";

export type SMRTRecurrenceType =
  | "none"
  | "weekdays"
  | "selected"
  | "weekly"
  | "daily"
  | "monthly"
  | "quarterly";

export interface SMRTRecurrence {
  type: SMRTRecurrenceType;
  daysOfWeek?: number[];
  everyXWeeks?: number;
  everyXDays?: number;
  // Day of month for monthly/quarterly. 31 in a shorter month is clamped
  // to the last day of that month when the next instance is generated.
  dayOfMonth?: number;
  // Days before the next instance's due date that the task becomes
  // visible in users' lists. Server clamps per type so it can never
  // exceed the recurrence interval. Not used for "Every weekday" or
  // "Daily" — those always become visible immediately.
  showDaysBeforeDue?: number;
  // ISO date (YYYY-MM-DD). When the next computed instance would fall
  // past this date, the server stops generating. Absent → repeats forever.
  endDate?: string;
}

export interface SMRTTaskFormData {
  title: string;
  description?: string;
  taskTypeId: string;
  assignedUserIds: string[];
  dueDate?: string;
  recurrence?: SMRTRecurrence | null;
  // Lead-time window for a single (non-recurring) task: hide it from lists
  // until this many days before its due date. Ignored by the server when a
  // recurrence is set. Omitted/undefined → visible immediately.
  showDaysBeforeDue?: number;
}

export interface SMRTTaskInitialData extends SMRTTaskFormData {
  isPrivate?: boolean;
  // Present on auto-generated recurring instances; surfaced as a small
  // "Part of recurring series" badge so the user knows edits apply to
  // this instance only.
  recurrenceParentId?: string;
}

export interface SMRTTasksAddTaskModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  initialData?: SMRTTaskInitialData;
  onClose: () => void;
  onSubmit: (payload: SMRTTaskFormData) => Promise<void> | void;
  taskTypes: { id: string; name: string }[];
  users: { user_id: string; name?: string; email: string }[];
  // When false, the caller is an assignee (not the creator) editing this
  // task. The modal restricts edits to dueDate + assignedUserIds and locks
  // the creator's checkbox so they can't be removed. Defaults to true so
  // create-mode and creator-edit paths render unchanged.
  canEditAllFields?: boolean;
  // Creator's user_id, used to lock that row in the assignee picker when
  // canEditAllFields is false. Required only for non-creator edits.
  creatorUserId?: string;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// UI-only enum. "everyday" is sugar for `{ type: "daily", everyXDays: 1 }`
// — schema stays unchanged, modal just exposes it as a distinct option so
// the user doesn't have to type "1" into the Every X Days input.
type RecurrenceUIType = SMRTRecurrenceType | "everyday";

export default function SMRTTasksAddTaskModal({
  isOpen,
  mode,
  initialData,
  onClose,
  onSubmit,
  taskTypes,
  users,
  canEditAllFields = true,
  creatorUserId,
}: SMRTTasksAddTaskModalProps) {
  const restricted = mode === "edit" && !canEditAllFields;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskTypeId, setTaskTypeId] = useState("");
  const [assigned, setAssigned] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceUIType>("none");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [everyXWeeks, setEveryXWeeks] = useState<number>(1);
  const [everyXDays, setEveryXDays] = useState<number>(2);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [showDaysBeforeDue, setShowDaysBeforeDue] = useState<number>(3);
  // Single (non-recurring) task lead-time. When ticked, the task stays hidden
  // from lists until `singleLeadDays` before its due date — the non-repeating
  // counterpart to the recurrence lead-time window above.
  const [singleLeadEnabled, setSingleLeadEnabled] = useState<boolean>(false);
  const [singleLeadDays, setSingleLeadDays] = useState<number>(7);
  // End-date mode for repeating tasks. Defaults to "no end" so existing
  // tasks open in the most permissive state.
  const [endMode, setEndMode] = useState<"none" | "date">("none");
  const [endDate, setEndDate] = useState<string>("");
  // Popup that fires when the user tries to pick a Repeat option before
  // setting a due date — the cadence engine anchors on dueDate so it must
  // be set first.
  const [showDueDateGate, setShowDueDateGate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "edit" && initialData) {
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setTaskTypeId(initialData.taskTypeId || taskTypes[0]?.id || "");
      setAssigned(initialData.assignedUserIds || []);
      setDueDate(
        initialData.dueDate ? initialData.dueDate.slice(0, 10) : "",
      );
      setSingleLeadEnabled(!!initialData.showDaysBeforeDue);
      setSingleLeadDays(initialData.showDaysBeforeDue || 7);
      const rec = initialData.recurrence;
      if (rec && rec.type) {
        // Reverse-map daily-with-everyXDays=1 to the "everyday" UI option
        // so the dropdown reflects what the user originally picked.
        const uiType: RecurrenceUIType =
          rec.type === "daily" && (rec.everyXDays || 1) === 1
            ? "everyday"
            : rec.type;
        setRecurrenceType(uiType);
        setSelectedDays(rec.daysOfWeek || []);
        setEveryXWeeks(rec.everyXWeeks || 1);
        setEveryXDays(Math.max(2, rec.everyXDays || 2));
        setDayOfMonth(rec.dayOfMonth || 1);
        setShowDaysBeforeDue(rec.showDaysBeforeDue || 3);
        if (rec.endDate) {
          setEndMode("date");
          setEndDate(rec.endDate.slice(0, 10));
        } else {
          setEndMode("none");
          setEndDate("");
        }
      } else {
        setRecurrenceType("none");
        setSelectedDays([]);
        setEveryXWeeks(1);
        setEveryXDays(2);
        setDayOfMonth(1);
        setShowDaysBeforeDue(3);
        setEndMode("none");
        setEndDate("");
      }
    } else {
      setTitle("");
      setDescription("");
      setTaskTypeId(taskTypes[0]?.id || "");
      setAssigned([]);
      setDueDate("");
      setRecurrenceType("none");
      setSelectedDays([]);
      setEveryXWeeks(1);
      setEveryXDays(2);
      setDayOfMonth(1);
      setShowDaysBeforeDue(3);
      setSingleLeadEnabled(false);
      setSingleLeadDays(7);
      setEndMode("none");
      setEndDate("");
    }
    setShowDueDateGate(false);
    setError(null);
    setSaving(false);
    setTimeout(() => titleRef.current?.focus(), 0);
    // `taskTypes` is intentionally omitted from the dep list. The body
    // reads taskTypes[0]?.id only to seed a default selection when the
    // modal opens; once the user is filling the form, a silent refetch
    // of taskTypes (from the parent's background polling) must NOT
    // re-fire this effect and wipe their in-progress input.
  }, [isOpen, mode, initialData]);

  // Backdrop click handler — must be called before the early return so the
  // hook order stays stable across renders. The inline closure captures the
  // latest `saving` from this render, matching handleClose's guard below.
  const backdrop = useModalBackdropClose(() => {
    if (saving) return;
    onClose();
  });

  if (!isOpen) return null;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const toggleAssignee = (userId: string) => {
    setAssigned((prev) =>
      prev.includes(userId)
        ? prev.filter((u) => u !== userId)
        : [...prev, userId],
    );
  };

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );
  };

  const buildRecurrence = (): SMRTRecurrence | null => {
    if (recurrenceType === "none") return null;
    const lead = Math.min(90, Math.max(1, showDaysBeforeDue));
    const stop = endMode === "date" && endDate ? endDate : undefined;
    // Daily and weekday recurrences become visible the moment the previous
    // instance completes — no lead window needed, so showDaysBeforeDue is
    // omitted (matches the server's isShortInterval branch).
    if (recurrenceType === "everyday") {
      return {
        type: "daily",
        everyXDays: 1,
        ...(stop ? { endDate: stop } : {}),
      };
    }
    if (recurrenceType === "weekdays") {
      return {
        type: "weekdays",
        ...(stop ? { endDate: stop } : {}),
      };
    }
    if (recurrenceType === "selected") {
      if (selectedDays.length === 0) return null;
      return {
        type: "selected",
        daysOfWeek: selectedDays,
        showDaysBeforeDue: lead,
        ...(stop ? { endDate: stop } : {}),
      };
    }
    if (recurrenceType === "weekly") {
      return {
        type: "weekly",
        everyXWeeks: Math.max(1, everyXWeeks),
        showDaysBeforeDue: lead,
        ...(stop ? { endDate: stop } : {}),
      };
    }
    if (recurrenceType === "daily") {
      return {
        type: "daily",
        everyXDays: Math.max(2, everyXDays),
        showDaysBeforeDue: lead,
        ...(stop ? { endDate: stop } : {}),
      };
    }
    if (recurrenceType === "monthly" || recurrenceType === "quarterly") {
      return {
        type: recurrenceType,
        dayOfMonth: Math.min(31, Math.max(1, dayOfMonth)),
        showDaysBeforeDue: lead,
        ...(stop ? { endDate: stop } : {}),
      };
    }
    return {
      type: recurrenceType,
      showDaysBeforeDue: lead,
      ...(stop ? { endDate: stop } : {}),
    };
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!taskTypeId) {
      setError("Task type is required");
      return;
    }
    if (assigned.length === 0) {
      setError("Select at least one assignee");
      return;
    }
    if (recurrenceType === "selected" && selectedDays.length === 0) {
      setError("Pick at least one day for the selected-days repeat");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        taskTypeId,
        assignedUserIds: assigned,
        dueDate: dueDate || undefined,
        recurrence: buildRecurrence(),
        // Single-task lead time only applies to non-recurring tasks with a
        // due date. Recurring tasks carry their lead time inside recurrence.
        showDaysBeforeDue:
          recurrenceType === "none" && singleLeadEnabled && dueDate
            ? Math.min(90, Math.max(1, singleLeadDays))
            : undefined,
      });
    } catch (err: unknown) {
      setError((err as Error)?.message || "Failed to save");
      setSaving(false);
    }
  };

  const heading = mode === "create" ? "Add Task" : "Edit Task";
  const saveLabel = saving
    ? "Saving..."
    : mode === "create"
      ? "Create Task"
      : "Save Changes";

  return (
    <div className="smrttasks-modal-overlay" {...backdrop}>
      <div
        className="smrttasks-modal smrttasks-modal-wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="smrttasks-modal-header">
          <h2>{heading}</h2>
        </div>
        <div className="smrttasks-modal-body">
          {error && <p className="smrttasks-modal-error">{error}</p>}

          {mode === "edit" && initialData?.recurrenceParentId && (
            <p className="smrttasks-modal-badge">
              Part of a recurring series — changes apply to this instance only.
            </p>
          )}

          {restricted && (
            <p className="smrttasks-modal-hint">
              You can update the due date and assignees. The task's creator
              manages the other fields.
            </p>
          )}

          <div className="smrttasks-form-row">
            <label htmlFor="smrttasks-task-title">Title</label>
            <input
              id="smrttasks-task-title"
              ref={titleRef}
              type="text"
              className="smrttasks-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={saving || restricted}
              placeholder="What needs to be done?"
            />
          </div>

          <div className="smrttasks-form-row">
            <label htmlFor="smrttasks-task-desc">Description</label>
            <textarea
              id="smrttasks-task-desc"
              className="smrttasks-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              disabled={saving || restricted}
              placeholder="Optional context for the assignee"
            />
          </div>

          <div className="smrttasks-form-row">
            <label htmlFor="smrttasks-task-type">Task Type</label>
            <select
              id="smrttasks-task-type"
              className="smrttasks-input"
              value={taskTypeId}
              onChange={(e) => setTaskTypeId(e.target.value)}
              disabled={saving || restricted}
            >
              {taskTypes.length === 0 && (
                <option value="">No task types — create one in Manage</option>
              )}
              {taskTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="smrttasks-form-row">
            <label>Assign To</label>
            <div className="smrttasks-checkbox-list">
              {users.length === 0 && (
                <div className="smrttasks-empty">No users available</div>
              )}
              {users.map((u) => {
                // Lock the creator's checkbox for restricted (non-creator)
                // edits so an assignee can't remove the creator from a task
                // they own. Mirrors the server-side guardrail.
                const isCreatorRow =
                  restricted &&
                  creatorUserId !== undefined &&
                  u.user_id === creatorUserId &&
                  assigned.includes(u.user_id);
                return (
                  <label key={u.user_id} className="smrttasks-checkbox-row">
                    <input
                      type="checkbox"
                      checked={assigned.includes(u.user_id)}
                      onChange={() => toggleAssignee(u.user_id)}
                      disabled={saving || isCreatorRow}
                      title={
                        isCreatorRow
                          ? "Only the creator can remove themselves"
                          : undefined
                      }
                    />
                    <span>
                      {u.name || u.email}
                      {isCreatorRow && (
                        <span className="smrttasks-checkbox-tag"> (creator)</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="smrttasks-form-row smrttasks-form-row-inline">
            <div style={{ flex: 1 }}>
              <label htmlFor="smrttasks-task-due">Due Date</label>
              <input
                id="smrttasks-task-due"
                type="date"
                className="smrttasks-input"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={saving}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="smrttasks-task-recur">Repeat</label>
              <select
                id="smrttasks-task-recur"
                className="smrttasks-input"
                value={recurrenceType}
                onChange={(e) => {
                  const next = e.target.value as RecurrenceUIType;
                  // Cadence anchors on dueDate, so refuse to leave "none"
                  // without one. Popup explains why instead of silently
                  // reverting.
                  if (next !== "none" && !dueDate) {
                    setShowDueDateGate(true);
                    return;
                  }
                  setRecurrenceType(next);
                }}
                disabled={saving || restricted}
              >
                <option value="none">Does not Repeat</option>
                <option value="everyday">Daily</option>
                <option value="weekdays">Every Week Day (Mon–Fri)</option>
                <option value="selected">On Selected Days</option>
                <option value="weekly">Weekly</option>
                <option value="daily">Every X Days</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>

          {recurrenceType === "none" && (
            <div className="smrttasks-form-row">
              <label className="smrttasks-checkbox-row">
                <input
                  type="checkbox"
                  checked={singleLeadEnabled}
                  onChange={(e) => setSingleLeadEnabled(e.target.checked)}
                  disabled={saving || restricted || !dueDate}
                />
                <span>Delay showing this task until closer to its due date</span>
              </label>
              {singleLeadEnabled && dueDate && (
                <div className="smrttasks-recur-inline">
                  <span className="smrttasks-recur-suffix">Show</span>
                  <input
                    id="smrttasks-task-single-lead"
                    type="number"
                    min={1}
                    max={90}
                    className="smrttasks-input smrttasks-input-narrow"
                    value={singleLeadDays}
                    onChange={(e) =>
                      setSingleLeadDays(
                        Math.min(
                          90,
                          Math.max(1, parseInt(e.target.value, 10) || 1),
                        ),
                      )
                    }
                    disabled={saving || restricted}
                  />
                  <span className="smrttasks-recur-suffix">
                    day(s) before due
                  </span>
                </div>
              )}
              {!dueDate && (
                <span className="smrttasks-recur-hint">
                  Set a due date to use this option.
                </span>
              )}
            </div>
          )}

          {recurrenceType === "selected" && (
            <div className="smrttasks-form-row">
              <label>Repeat on</label>
              <div className="smrttasks-day-picker">
                {DAY_LABELS.map((label, idx) => (
                  <label key={idx} className="smrttasks-day-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(idx)}
                      onChange={() => toggleDay(idx)}
                      disabled={saving || restricted}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {recurrenceType === "weekly" && (
            <div className="smrttasks-form-row">
              <label htmlFor="smrttasks-task-weekly-n">Every</label>
              <div className="smrttasks-recur-inline">
                <input
                  id="smrttasks-task-weekly-n"
                  type="number"
                  min={1}
                  max={52}
                  className="smrttasks-input smrttasks-input-narrow"
                  value={everyXWeeks}
                  onChange={(e) =>
                    setEveryXWeeks(
                      Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                  disabled={saving || restricted}
                />
                <span className="smrttasks-recur-suffix">
                  week(s)
                </span>
              </div>
            </div>
          )}

          {recurrenceType === "daily" && (
            <div className="smrttasks-form-row">
              <label htmlFor="smrttasks-task-daily-n">Every</label>
              <div className="smrttasks-recur-inline">
                <input
                  id="smrttasks-task-daily-n"
                  type="number"
                  min={2}
                  max={365}
                  className="smrttasks-input smrttasks-input-narrow"
                  value={everyXDays}
                  onChange={(e) =>
                    setEveryXDays(
                      Math.max(2, parseInt(e.target.value, 10) || 2),
                    )
                  }
                  disabled={saving || restricted}
                />
                <span className="smrttasks-recur-suffix">day(s)</span>
              </div>
            </div>
          )}

          {(recurrenceType === "monthly" ||
            recurrenceType === "quarterly") && (
            <div className="smrttasks-form-row">
              <label htmlFor="smrttasks-task-dom">Day of month</label>
              <div className="smrttasks-recur-inline">
                <input
                  id="smrttasks-task-dom"
                  type="number"
                  min={1}
                  max={31}
                  className="smrttasks-input smrttasks-input-narrow"
                  value={dayOfMonth}
                  onChange={(e) =>
                    setDayOfMonth(
                      Math.min(
                        31,
                        Math.max(1, parseInt(e.target.value, 10) || 1),
                      ),
                    )
                  }
                  disabled={saving || restricted}
                />
                <span className="smrttasks-recur-hint">
                  Day 29–31 falls back to the last day of months that don't
                  have it.
                </span>
              </div>
            </div>
          )}

          {recurrenceType !== "none" &&
            recurrenceType !== "everyday" &&
            recurrenceType !== "weekdays" && (
              <div className="smrttasks-form-row">
                <label htmlFor="smrttasks-task-lead">Show next instance</label>
                <div className="smrttasks-recur-inline">
                  <input
                    id="smrttasks-task-lead"
                    type="number"
                    min={1}
                    max={90}
                    className="smrttasks-input smrttasks-input-narrow"
                    value={showDaysBeforeDue}
                    onChange={(e) =>
                      setShowDaysBeforeDue(
                        Math.min(
                          90,
                          Math.max(1, parseInt(e.target.value, 10) || 1),
                        ),
                      )
                    }
                    disabled={saving || restricted}
                  />
                  <span className="smrttasks-recur-suffix">day(s) before due</span>
                </div>
              </div>
            )}

          {recurrenceType !== "none" && (
            <div className="smrttasks-form-row">
              <label>When should it stop repeating?</label>
              <div className="smrttasks-recur-end">
                <label className="smrttasks-radio-row">
                  <input
                    type="radio"
                    name="smrttasks-end-mode"
                    value="none"
                    checked={endMode === "none"}
                    onChange={() => {
                      setEndMode("none");
                      setEndDate("");
                    }}
                    disabled={saving || restricted}
                  />
                  <span>No End Date</span>
                </label>
                <label className="smrttasks-radio-row">
                  <input
                    type="radio"
                    name="smrttasks-end-mode"
                    value="date"
                    checked={endMode === "date"}
                    onChange={() => setEndMode("date")}
                    disabled={saving || restricted}
                  />
                  <span>Stop on</span>
                  <input
                    type="date"
                    className="smrttasks-input smrttasks-input-narrow"
                    value={endDate}
                    min={dueDate || undefined}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (e.target.value) setEndMode("date");
                    }}
                    disabled={saving || restricted || endMode !== "date"}
                  />
                </label>
              </div>
            </div>
          )}

          <div className="smrttasks-modal-actions">
            <button
              type="button"
              className="smrttasks-btn-cancel"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="smrttasks-btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saveLabel}
            </button>
          </div>
        </div>
      </div>

      {showDueDateGate && (
        <div
          className="smrttasks-modal-overlay smrttasks-modal-overlay-stacked"
          onClick={() => setShowDueDateGate(false)}
        >
          <div
            className="smrttasks-modal smrttasks-modal-narrow"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrttasks-modal-header">
              <h2>Due Date Required</h2>
            </div>
            <div className="smrttasks-modal-body">
              <p>Please select a due date to enable Repeat options.</p>
              <div className="smrttasks-modal-actions">
                <button
                  type="button"
                  className="smrttasks-btn-save"
                  onClick={() => setShowDueDateGate(false)}
                  autoFocus
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
